import React from "react";
import {Box, Chip, IconButton, Link, Paper, Stack, Typography, useTheme} from "@mui/material";
import {OpenInNew, ChevronRight, EditNote} from "@mui/icons-material";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {LimitedResourceResponse} from "../../data/LimitedResourceResponse";
import {UserContextProps} from "../../hooks/useUser";
import DeleteResourcePrompt from "../../pages/resource/DeleteResourcePrompt";
import {formatRelativeTime, getResourceTypeColor, getResourceTypeLabel} from "./resourceUtils";

interface ResourceCardProps {
    resource: LimitedResourceResponse;
    divisionIdentifier: string;
    divisionDisplayName?: string;
    user: UserContextProps;
    showDivision?: boolean;
}

export function ResourceCard({resource, divisionIdentifier, divisionDisplayName, user, showDivision}: ResourceCardProps) {
    const theme = useTheme();
    const navigate = useNavigate();

    const typeColor = getResourceTypeColor(resource.type);
    const typeLabel = getResourceTypeLabel(resource.type);

    const handleClick = () => {
        if (resource.type === "content") {
            navigate(`/resource/${divisionIdentifier.replaceAll("_", "-")}/${resource.identifier}`);
        } else {
            window.open(resource.content, "_blank");
        }
    };

    const handleEdit = () => {
        const divUrl = divisionIdentifier.replaceAll("_", "-");
        navigate(`/resources/edit/${divUrl}/${resource.identifier}`, {
            state: {
                id: resource.id,
                title: resource.title,
                type: resource.type,
                content: resource.content,
                privateResource: resource.privateResource,
                identifier: resource.identifier,
            }
        });
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 1.5,
                cursor: "pointer",
                transition: "border-color 0.2s, background-color 0.2s",
                "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover,
                },
                display: "flex",
                alignItems: "center",
            }}
            onClick={handleClick}
        >
            <Chip
                label={typeLabel}
                size="small"
                sx={{
                    backgroundColor: typeColor,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    mr: 2,
                    minWidth: 80,
                    borderRadius: "4px",
                }}
            />
            <Box sx={{flexGrow: 1, minWidth: 0}}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body1" sx={{fontWeight: 500}} noWrap>
                        {resource.title}
                    </Typography>
                    {resource.type === "link" && (
                        <OpenInNew sx={{fontSize: 16, color: theme.palette.text.secondary}}/>
                    )}
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{mt: 0.5}}>
                    <Chip
                        label={resource.privateResource ? "Private" : "Public"}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            backgroundColor: resource.privateResource
                                ? "rgba(244, 67, 54, 0.15)"
                                : "rgba(76, 175, 80, 0.15)",
                            color: resource.privateResource
                                ? theme.palette.error.main
                                : "#4caf50",
                            fontWeight: 600,
                        }}
                    />
                    {showDivision && (
                        <Typography variant="caption" color="text.secondary">
                            in {divisionDisplayName || divisionIdentifier.replaceAll("_", " ")} · edited {formatRelativeTime(resource.date)} by{" "}
                            <Link
                                component={RouterLink}
                                to={`/user/${resource.editorCommunityId}`}
                                underline="hover"
                                color="primary"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {resource.editorNick}
                            </Link>
                        </Typography>
                    )}
                    {!showDivision && (
                        <Typography variant="caption" color="text.secondary">
                            edited {formatRelativeTime(resource.date)} by{" "}
                            <Link
                                component={RouterLink}
                                to={`/user/${resource.editorCommunityId}`}
                                underline="hover"
                                color="primary"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {resource.editorNick}
                            </Link>
                        </Typography>
                    )}
                </Stack>
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" onClick={(e) => e.stopPropagation()}>
                {user.hasPermissions(`${divisionIdentifier}-resource:modify`) && (
                    <IconButton size="small" color="info" onClick={handleEdit}>
                        <EditNote/>
                    </IconButton>
                )}
                {user.hasPermissions(`${divisionIdentifier}-resource:delete`) && (
                    <DeleteResourcePrompt
                        divisionIdentifier={divisionIdentifier}
                        id={resource.id}
                    />
                )}
            </Stack>
            <ChevronRight sx={{color: theme.palette.text.secondary, ml: 1}}/>
        </Paper>
    );
}
